// src/modules/accounting/pages/ChartOfAccountsPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Modal, Form, Input, Select, Tag, Space, message,
  Typography, Row, Col, Tooltip, Switch, Popconfirm, Alert, Spin, Badge,
  Descriptions, Statistic, Divider
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, EditOutlined,
  CheckCircleOutlined, StopOutlined, InfoCircleOutlined,
  ThunderboltOutlined, ApartmentOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import {
  getAccounts, getAccountTree, createAccount, updateAccount,
  bootstrapAccounts,
} from '../api';
import { ACCOUNT_NATURES, NATURE_MAP } from '../constants';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;

const TYPE_COLORS = {
  ASSET: 'blue',
  LIABILITY: 'red',
  EQUITY: 'purple',
  REVENUE: 'green',
  EXPENSE: 'orange',
};

const TYPE_RANGES = {
  ASSET: '1xxx',
  LIABILITY: '2xxx',
  EQUITY: '3xxx',
  REVENUE: '4xxx',
  EXPENSE: '5xxx',
};

// Nature dropdown options grouped by financial statement
const NATURE_SELECT_OPTIONS = ['Balance Sheet', 'Profit & Loss'].map(stmt => ({
  label: stmt,
  options: ACCOUNT_NATURES
    .filter(n => n.statement === stmt)
    .map(n => ({ value: n.value, label: n.label })),
}));

// Tag for an account's nature; falls back for legacy rows without one
const natureTag = (nature) => {
  const def = NATURE_MAP[nature];
  if (!def) return <Tag>Unclassified</Tag>;
  return <Tag color={TYPE_COLORS[def.type]}>{def.label}</Tag>;
};

export default function ChartOfAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('flat'); // 'flat' | 'tree'
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [natureFilter, setNatureFilter] = useState(null);
  const [form] = Form.useForm();

  // ── Fetch accounts ──
  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const [flatRes, treeRes] = await Promise.all([
        getAccounts({ limit: 500 }),
        getAccountTree(),
      ]);
      const flatData = flatRes.data || flatRes || [];
      setAccounts(Array.isArray(flatData) ? flatData : []);
      const tree = treeRes.data || treeRes || [];
      setTreeData(Array.isArray(tree) ? tree : []);
    } catch (err) {
      message.error('Failed to load accounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // ── Bootstrap default accounts ──
  const handleBootstrap = async () => {
    try {
      const res = await bootstrapAccounts();
      const data = res.data || res;
      message.success(`Bootstrap complete: ${data.created} created, ${data.skipped} already existed`);
      fetchAccounts();
    } catch (err) {
      message.error(err.message || 'Bootstrap failed');
    }
  };

  // ── Hierarchy helpers ──
  // Ids of everything nested under `rootId`. Selecting one of these as a parent
  // would create a loop, so they are filtered out of the picker (the backend
  // rejects it too — this just stops the user getting that far).
  const descendantIdsOf = useCallback((rootId) => {
    const childrenOf = new Map();
    accounts.forEach((a) => {
      const pid = a.parentAccount?._id || a.parentAccount;
      if (!pid) return;
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid).push(a._id);
    });

    const out = new Set();
    const queue = [rootId];
    while (queue.length > 0) {
      const next = queue.shift();
      for (const childId of childrenOf.get(next) || []) {
        if (out.has(childId)) continue;   // guards a corrupt cyclic graph
        out.add(childId);
        queue.push(childId);
      }
    }
    return out;
  }, [accounts]);

  // ── Open create/edit modal ──
  const openCreateModal = () => {
    setEditingAccount(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingAccount(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      nature: record.nature || null,
      description: record.description || '',
      parentAccount: record.parentAccount?._id || record.parentAccount || null,
    });
    setModalOpen(true);
  };

  // ── Save (create or update) ──
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingAccount) {
        // Send only fields that actually changed — the backend rejects
        // untouchable fields (e.g. code) on system accounts even if unchanged.
        const original = {
          code: editingAccount.code || null,
          name: editingAccount.name || null,
          nature: editingAccount.nature || null,
          description: editingAccount.description || '',
          parentAccount: editingAccount.parentAccount?._id || editingAccount.parentAccount || null,
        };
        const changed = {};
        Object.keys(values).forEach((k) => {
          const next = values[k] === undefined ? null : values[k];
          const prev = original[k] === undefined ? null : original[k];
          if (next !== prev) changed[k] = next;
        });

        if (Object.keys(changed).length === 0) {
          message.info('No changes to save');
        } else {
          await updateAccount(editingAccount._id, changed);
          message.success('Account updated');
        }
      } else {
        await createAccount(values);
        message.success('Account created');
      }

      setModalOpen(false);
      form.resetFields();
      setEditingAccount(null);
      fetchAccounts();
    } catch (err) {
      if (err.errorFields) return; // form validation
      message.error(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle enable/disable ──
  const handleToggleStatus = async (record) => {
    try {
      const endpoint = record.isActive ? 'disable' : 'enable';
      const response = await fetch(`/api/v1/accounting/accounts/${record._id}/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message);

      message.success(`Account ${record.isActive ? 'disabled' : 'enabled'}`);
      if (data.warning) message.warning(data.warning);
      fetchAccounts();
    } catch (err) {
      message.error(err.message || 'Toggle failed');
    }
  };

  // ── Stats ──
  const stats = {
    total: accounts.length,
    active: accounts.filter(a => a.isActive).length,
    balanceSheet: accounts.filter(a => NATURE_MAP[a.nature]?.statement === 'Balance Sheet').length,
    profitLoss: accounts.filter(a => NATURE_MAP[a.nature]?.statement === 'Profit & Loss').length,
    unclassified: accounts.filter(a => !NATURE_MAP[a.nature]).length,
  };

  // ── Filtered data ──
  const filteredAccounts = natureFilter
    ? accounts.filter(a => natureFilter === 'UNCLASSIFIED' ? !a.nature : a.nature === natureFilter)
    : accounts;

  // ── Table columns ──
  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 90,
      sorter: (a, b) => a.code.localeCompare(b.code),
      defaultSortOrder: 'ascend',
      render: (code) => <Text strong style={{ fontFamily: 'monospace' }}>{code}</Text>,
    },
    {
      title: 'Account Name',
      dataIndex: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Nature',
      dataIndex: 'nature',
      width: 200,
      filters: [
        ...ACCOUNT_NATURES.map(n => ({ text: n.label, value: n.value })),
        { text: 'Unclassified', value: 'UNCLASSIFIED' },
      ],
      onFilter: (val, rec) => (val === 'UNCLASSIFIED' ? !rec.nature : rec.nature === val),
      render: (nature) => natureTag(nature),
    },
    {
      title: 'Statement',
      dataIndex: 'nature',
      key: 'statement',
      width: 110,
      render: (nature) => NATURE_MAP[nature]
        ? <Text type="secondary" style={{ fontSize: 12 }}>{NATURE_MAP[nature].statement}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 90,
      filters: [{ text: 'Active', value: true }, { text: 'Inactive', value: false }],
      onFilter: (val, rec) => rec.isActive === val,
      render: (active) => active
        ? <Badge status="success" text="Active" />
        : <Badge status="error" text="Inactive" />,
    },
    {
      title: 'System',
      dataIndex: 'isSystemAccount',
      width: 80,
      render: (v) => v ? <Tag color="geekblue">System</Tag> : <Tag>Custom</Tag>,
    },
    {
      title: 'Rolls up into',
      dataIndex: 'parentAccount',
      width: 190,
      render: (parent, rec) => {
        if (parent?.code) {
          return (
            <Tooltip title={`This account's balance is included in ${parent.code} ${parent.name}`}>
              <Text type="secondary"><ApartmentOutlined /> {parent.code} — {parent.name}</Text>
            </Tooltip>
          );
        }
        return rec.hasChildren
          ? <Tag color="geekblue">Group of {rec.descendantCount}</Tag>
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Linked Fund',
      dataIndex: 'linkedFundId',
      width: 130,
      render: (fund) => fund
        ? <Tag color="cyan">{fund.fundId || fund.name}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View details">
            <Button size="small" icon={<InfoCircleOutlined />} onClick={() => setDetailModal(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Popconfirm
            title={`${record.isActive ? 'Disable' : 'Enable'} this account?`}
            onConfirm={() => handleToggleStatus(record)}
          >
            <Tooltip title={record.isActive ? 'Disable' : 'Enable'}>
              <Button
                size="small"
                danger={record.isActive}
                icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Tree columns (simplified) ──
  const treeColumns = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 100,
      render: (code) => <Text strong style={{ fontFamily: 'monospace' }}>{code}</Text>,
    },
    {
      title: 'Account Name',
      dataIndex: 'name',
      ellipsis: true,
      render: (name, rec) => (
        <Space size={4}>
          <span>{name}</span>
          {rec.hasChildren && (
            <Tooltip title={`Group account — ${rec.childCount} direct child account(s), ${rec.descendantCount} in total. Its ledger and reports show the sum of the branch.`}>
              <Tag color="geekblue">Group · {rec.descendantCount}</Tag>
            </Tooltip>
          )}
          {rec.parentHidden && (
            <Tooltip title="This account's parent is disabled, so it is shown at the top level here.">
              <Tag color="orange">Parent disabled</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Nature',
      dataIndex: 'nature',
      width: 200,
      render: (nature) => natureTag(nature),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 90,
      render: (active) => active
        ? <Badge status="success" text="Active" />
        : <Badge status="error" text="Inactive" />,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
    },
  ];

  // ── Prepare tree data for Ant Design Table ──
  const buildTreeTableData = (nodes) => {
    return nodes.map(node => ({
      ...node,
      key: node._id,
      children: node.children && node.children.length > 0
        ? buildTreeTableData(node.children)
        : undefined,
    }));
  };

  if (loading && accounts.length === 0) {
    return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  }

  return (
    <div>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>Chart of Accounts</Title>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Filter by nature"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 240 }}
              value={natureFilter}
              onChange={setNatureFilter}
              options={[
                ...NATURE_SELECT_OPTIONS,
                { label: 'Other', options: [{ value: 'UNCLASSIFIED', label: 'Unclassified' }] },
              ]}
            />
            <Button
              icon={viewMode === 'flat' ? <ApartmentOutlined /> : <UnorderedListOutlined />}
              onClick={() => setViewMode(v => v === 'flat' ? 'tree' : 'flat')}
            >
              {viewMode === 'flat' ? 'Tree View' : 'Flat View'}
            </Button>
            <Tooltip title="Bootstrap default accounts">
              <Popconfirm title="Seed default Chart of Accounts?" onConfirm={handleBootstrap}>
                <Button icon={<ThunderboltOutlined />}>Bootstrap</Button>
              </Popconfirm>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              New Account
            </Button>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchAccounts} loading={loading} />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {/* ── Stats ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6} md={4}>
          <Card size="small"><Statistic title="Total" value={stats.total} /></Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card size="small"><Statistic title="Active" value={stats.active} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card size="small"><Statistic title="Balance Sheet" value={stats.balanceSheet} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card size="small"><Statistic title="Profit & Loss" value={stats.profitLoss} valueStyle={{ color: '#fa8c16' }} /></Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card
            size="small"
            style={{ cursor: stats.unclassified > 0 ? 'pointer' : undefined, border: natureFilter === 'UNCLASSIFIED' ? '2px solid #ff4d4f' : undefined }}
            onClick={() => stats.unclassified > 0 && setNatureFilter(prev => prev === 'UNCLASSIFIED' ? null : 'UNCLASSIFIED')}
          >
            <Statistic
              title="Unclassified"
              value={stats.unclassified}
              valueStyle={{ color: stats.unclassified > 0 ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {stats.unclassified > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message={`${stats.unclassified} account(s) have no nature assigned`}
          description="Edit each account and pick its nature, or run the backfill script (scripts/backfillAccountNatures.js) to classify them in bulk."
        />
      )}

      {/* ── Table ── */}
      {viewMode === 'flat' ? (
        <Card size="small">
          <DataTable persistId="accounting.chart-of-accounts.flat"
            dataSource={filteredAccounts.map(a => ({ ...a, key: a._id }))}
            columns={columns}
            loading={loading}
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} accounts` }}
            size="small"
            scroll={{ x: 1000 }}
          />
        </Card>
      ) : (
        <Card size="small" title="Account Tree">
          {treeData.length > 0 ? (
            <DataTable persistId="accounting.chart-of-accounts.tree"
              dataSource={buildTreeTableData(treeData)}
              columns={treeColumns}
              pagination={false}
              size="small"
              expandable={{ defaultExpandAllRows: true }}
              scroll={{ x: 800 }}
            />
          ) : (
            <Alert message="No accounts found. Use Bootstrap to seed the default Chart of Accounts." type="info" showIcon />
          )}
        </Card>
      )}

      {/* ── Create / Edit Modal ── */}
      <Modal
        title={editingAccount ? `Edit Account — ${editingAccount.code}` : 'Create New Account'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingAccount(null); form.resetFields(); }}
        confirmLoading={saving}
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="e.g. 1500" disabled={!!editingAccount?.isSystemAccount} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="e.g. Prepaid Expenses" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="nature" label="Nature" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select where this account belongs"
              options={NATURE_SELECT_OPTIONS}
              onChange={(nature) => {
                // A parent must share the child's type. Changing nature can flip
                // the type, so clear a selection that would no longer be legal.
                const nextType = NATURE_MAP[nature]?.type;
                const parentId = form.getFieldValue('parentAccount');
                if (!parentId || !nextType) return;
                const parent = accounts.find(a => a._id === parentId);
                if (parent && parent.type !== nextType) {
                  form.setFieldsValue({ parentAccount: null });
                  message.info(`Parent cleared — ${parent.code} is ${parent.type}, this account is now ${nextType}`);
                }
              }}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.nature !== cur.nature}>
            {({ getFieldValue }) => {
              const def = NATURE_MAP[getFieldValue('nature')];
              if (!def) return null;
              return (
                <div style={{ marginTop: -16, marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {def.statement} · <Tag color={TYPE_COLORS[def.type]} style={{ fontSize: 10 }}>{def.type}</Tag>
                    {def.normalBalance === 'DEBIT' ? 'Debit' : 'Credit'}-normal · suggested code range {TYPE_RANGES[def.type]}
                  </Text>
                </div>
              );
            }}
          </Form.Item>
          {/* Parent picker. Offering every account is what allowed a loop to be
              created and a child to be nested under a different account type —
              both of which break the roll-up. Options are narrowed to accounts
              that can legally be this one's parent, and the hint below spells
              out what selecting one will do. */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.nature !== cur.nature}>
            {({ getFieldValue }) => {
              const natureDef = NATURE_MAP[getFieldValue('nature')];
              const effectiveType = natureDef?.type || editingAccount?.type || null;
              const blocked = editingAccount ? descendantIdsOf(editingAccount._id) : new Set();

              const options = accounts
                .filter(a => a.isActive)
                .filter(a => !editingAccount || a._id !== editingAccount._id)
                .filter(a => !blocked.has(a._id))
                .filter(a => !effectiveType || a.type === effectiveType)
                .map(a => ({
                  value: a._id,
                  label: `${a.code} — ${a.name}${a.hasChildren ? ` (${a.childCount} child)` : ''}`,
                }));

              return (
                <>
                  <Form.Item name="parentAccount" label="Parent Account (optional)">
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={effectiveType ? 'None (top-level)' : 'Pick a Nature first'}
                      disabled={!effectiveType}
                      options={options}
                      notFoundContent={`No other enabled ${effectiveType || ''} account is available as a parent`}
                    />
                  </Form.Item>

                  <Form.Item noStyle shouldUpdate={(prev, cur) => prev.parentAccount !== cur.parentAccount}>
                    {({ getFieldValue: gv }) => {
                      const parentId = gv('parentAccount');
                      const parent = parentId && accounts.find(a => a._id === parentId);
                      const selfChildren = editingAccount
                        ? accounts.filter(a => (a.parentAccount?._id || a.parentAccount) === editingAccount._id)
                        : [];

                      return (
                        <div style={{ marginTop: -12, marginBottom: 16 }}>
                          {parent ? (
                            <Alert
                              type="info" showIcon icon={<ApartmentOutlined />}
                              message={`Rolls up into ${parent.code} — ${parent.name}`}
                              description={
                                <Text style={{ fontSize: 12 }}>
                                  This account&apos;s balance will be included in{' '}
                                  <Text strong>{parent.code}</Text>&apos;s General Ledger, Trial Balance and
                                  Balance Sheet totals as soon as you save. It still keeps its own ledger.
                                </Text>
                              }
                            />
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <InfoCircleOutlined /> Top-level account — its balance is reported on its own.
                            </Text>
                          )}
                          {selfChildren.length > 0 && (
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                              This is a group account: {selfChildren.map(c => c.code).join(', ')} roll up into it.
                              Accounts underneath it are not offered as its parent.
                            </Text>
                          )}
                        </div>
                      );
                    }}
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Detail Modal ── */}
      <Modal
        title={detailModal ? `Account: ${detailModal.code} — ${detailModal.name}` : 'Account Detail'}
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={null}
        width={520}
      >
        {detailModal && (
          <Descriptions column={2} bordered size="small" style={{ marginTop: 8 }}>
            <Descriptions.Item label="Account ID">{detailModal.accountId}</Descriptions.Item>
            <Descriptions.Item label="Code">
              <Text strong style={{ fontFamily: 'monospace' }}>{detailModal.code}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Name" span={2}>{detailModal.name}</Descriptions.Item>
            <Descriptions.Item label="Nature" span={2}>
              {natureTag(detailModal.nature)}
              {NATURE_MAP[detailModal.nature] && (
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  {NATURE_MAP[detailModal.nature].statement}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={TYPE_COLORS[detailModal.type]}>{detailModal.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Normal Balance">
              {detailModal.normalBalance || (NATURE_MAP[detailModal.nature]?.normalBalance ?? '—')}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {detailModal.isActive
                ? <Badge status="success" text="Active" />
                : <Badge status="error" text="Inactive" />}
            </Descriptions.Item>
            <Descriptions.Item label="System Account">
              {detailModal.isSystemAccount ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Parent">
              {detailModal.parentAccount?.code
                ? `${detailModal.parentAccount.code} — ${detailModal.parentAccount.name} (balance rolls up here)`
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Linked Fund" span={2}>
              {detailModal.linkedFundId
                ? `${detailModal.linkedFundId.fundId || ''} — ${detailModal.linkedFundId.name || ''}`
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {detailModal.description || '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
